import { useId, useState } from 'react';
import { useUnsavedChanges } from '../../lib/use-unsaved-changes.js';

/**
 * The integration types the form can add, in the order of the Add list. A new type (Jira, ServiceNow, email to a ticket inbox)
 * adds one entry here and its id to the API's integrationTypes; nothing else in the tab changes. `secretFields` are never
 * prefilled and never shown again after saving.
 */
export const TYPE_CONFIG = {
  teams: {
    label: 'Microsoft Teams',
    addLabel: 'Teams',
    secretFields: [{
      name: 'webhookUrl',
      label: 'Workflow webhook URL',
      help: "Create a workflow from the 'Send webhook alerts to a channel' template in Teams and paste its URL. Add a co-owner in Teams, or the alerts stop when this person leaves.",
    }],
  },
};

/** Plain sentences for the codes the API answers (portal/shared/integrations.mjs, routes/company-integrations.mjs). */
export const ERROR_TEXT = {
  invalid_label: 'Give the integration a name of up to 60 characters.',
  invalid_url: 'That is not a valid web address.',
  not_https: 'The address must start with https://.',
  userinfo: 'The address must not contain a user name or password.',
  port: 'The address must use the standard https port.',
  ip_literal: 'The address must use a host name, not an IP address.',
  host_not_allowed: 'That is not a Microsoft Teams workflow address. Copy the URL Teams shows for the workflow.',
  too_long: 'The address is too long.',
  type_not_available: 'This integration type is not available yet.',
  limit_reached: 'A company can have at most 5 integrations. Remove one first.',
  secret_write_failed: 'The connection could not be saved in your account. Try again in a few minutes.',
  secret_delete_failed: 'The connection could not be removed from your account. Try again in a few minutes.',
  not_deployed: 'The onGuard collector is not deployed for this company yet.',
  rate_limited: 'You can test a connection 10 times an hour. Try again later.',
  'claim-race': 'The settings were changed by someone else. Reload and try again.',
  feature_disabled: 'Ticket integrations are not available.',
};
export const errorText = (e) => ERROR_TEXT[e?.body?.error] ?? ERROR_TEXT[e?.body?.details?.reason] ?? 'That did not work. Try again in a few minutes.';

/**
 * Add form (one type) and, with `rotate`, the replace-the-secret form. The secret fields are password inputs with autocomplete
 * off, start empty, and are cleared after a successful save by the parent unmounting the form.
 */
export default function IntegrationForm({ type, rotate = false, busy, error, onSubmit, onCancel }) {
  const id = useId();
  const cfg = TYPE_CONFIG[type];
  const [label, setLabel] = useState('');
  const [secrets, setSecrets] = useState({});
  // The parent unmounts the form on a successful save and on Cancel, which clears the flag.
  useUnsavedChanges(label.trim() !== '' || Object.values(secrets).some((v) => (v ?? '').trim() !== ''));
  if (!cfg) return null;
  const ready = cfg.secretFields.every((f) => (secrets[f.name] ?? '').trim() !== '') && (rotate || label.trim() !== '');

  function submit(e) {
    e.preventDefault();
    if (busy || !ready) return;
    const values = Object.fromEntries(cfg.secretFields.map((f) => [f.name, secrets[f.name].trim()]));
    onSubmit(rotate ? values : { type, label: label.trim(), ...values });
  }

  return (
    <form className="space-y-3 rounded border border-slate-200 p-3" onSubmit={submit} aria-label={rotate ? `Replace the ${cfg.label} connection` : `Add ${cfg.label}`}>
      {rotate ? null : (
        <div>
          <label htmlFor={`${id}-label`} className="block text-sm font-medium">Name</label>
          <input id={`${id}-label`} className="w-full max-w-md rounded border border-slate-300 px-2 py-1 text-sm" value={label} maxLength={60} onChange={(e) => setLabel(e.target.value)} autoComplete="off" spellCheck={false} />
          <p className="text-xs text-slate-500">Shown in this list, for example the name of the channel.</p>
        </div>
      )}
      {cfg.secretFields.map((f) => (
        <div key={f.name}>
          <label htmlFor={`${id}-${f.name}`} className="block text-sm font-medium">{rotate ? `New ${f.label.toLowerCase()}` : f.label}</label>
          <input
            id={`${id}-${f.name}`}
            type="password"
            className="w-full max-w-xl rounded border border-slate-300 px-2 py-1 text-sm"
            value={secrets[f.name] ?? ''}
            onChange={(e) => setSecrets((s) => ({ ...s, [f.name]: e.target.value }))}
            autoComplete="off"
            spellCheck={false}
            aria-describedby={`${id}-${f.name}-help`}
          />
          <p id={`${id}-${f.name}-help`} className="text-xs text-slate-500">{f.help}</p>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className="btn-primary" disabled={busy || !ready}>{rotate ? 'Replace' : 'Add'}</button>
        <button type="button" className="btn-secondary" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
      {error ? <p role="alert" className="text-sm text-red-700">{error}</p> : null}
    </form>
  );
}
